import { c as _c } from "react/compiler-runtime";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useGiveXgSeats } from "@hooks/use-give-xg-seats";
import { areCompanyMembersLoadingSelector, companyMembersSelector, fetchedMembersFlagsSelector } from "@store/members/members-selector";
import { activeSeatsAvailableSelector, isScanPlanSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { assignableTeamsSelector } from "@store/teams/teams-selector";
import { getSelectedOptionValue, getSelectedOptionValues } from "@utils/members-invite-utils";
import { getBlockedMessage, getMembersByKey, getSelectionXgSeats, getTeamKeys, getXgSeatChipColor, NO_XG_SEAT_MESSAGE, TEAM_NOT_ALLOWED_MESSAGE } from "@utils/invite-xg-seat-utils";

/**
 * The checkbox of the Sphere XG seat that the selected members without one are given along the
 * invite. The role needs the seat, so it is always checked, unless none is left to give.
 */

/** How giving the seats to the selection went */

/** How the selection fares with the Sphere XG seats that the selected role needs */

/** Returns the keys of the people to give a seat: their lower-cased emails, each of them once */
function getSeatKeys({
  membersToSeat,
  emailsToSeat
}) {
  return [...membersToSeat.map(({
    email
  }) => email), ...emailsToSeat].map(email => email.toLowerCase());
}

/** Giving no seats */
const NO_FAILURES = {
  failures: [],
  numberOfSeatsGiven: 0,
  failedValues: new Set()
};

/** A selection that needs no seats, because of the plan or the role */
const SELECTION_WITHOUT_SEATS = {
  getChipColor: () => undefined,
  isValid: true,
  giveSeats: () => Promise.resolve(NO_FAILURES)
};

/**
 * Applies the Sphere XG seats to inviting members, e.g. to a project or a group. On a Scan plan,
 * only members that hold a Sphere XG seat can be given a role that needs one, and teams cannot be
 * given such a role at all.
 *
 * Users that manage the seats give one to the selected members without, as the role needs it, so
 * this cannot be opted out of, as in the workspace invite. The invite cannot be sent while the free
 * seats are too few for them. Everybody else can only select the members holding one, and so can
 * the users that manage the seats once no seat is left to pick a member without one from the list.
 */
export function useInviteXgSeats(t0) {
  const $ = _c(19);
  const {
    isSeatRequiredForRole,
    seatRequiredMessage,
    location
  } = t0;
  const {
    giveSeats
  } = useGiveXgSeats(location);
  const {
    canManageXgSeats
  } = useHasUserValidRoleCompanyLevel();
  const isScanPlan = useAppSelector(isScanPlanSelector);
  const companyMembers = useAppSelector(companyMembersSelector);
  const teams = useAppSelector(assignableTeamsSelector);
  const seatsAvailable = useAppSelector(activeSeatsAvailableSelector);
  const areMembersLoading = useAppSelector(areCompanyMembersLoadingSelector);
  const {
    hasFetchedWorkspaceMembers
  } = useAppSelector(fetchedMembersFlagsSelector);
  const isSeatRequired = isScanPlan && isSeatRequiredForRole;
  const isOutOfSeats = seatsAvailable === 0;
  const canSelectMembersWithoutSeat = canManageXgSeats && !isOutOfSeats;
  let t1;
  if ($[0] !== companyMembers) {
    t1 = getMembersByKey(companyMembers);
    $[0] = companyMembers;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const membersByKey = t1;
  let t2;
  if ($[2] !== teams) {
    t2 = getTeamKeys(teams);
    $[2] = teams;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const teamKeys = t2;
  let checkSelection;
  if ($[4] !== areMembersLoading || $[5] !== canManageXgSeats || $[6] !== giveSeats || $[7] !== hasFetchedWorkspaceMembers || $[8] !== isOutOfSeats || $[9] !== isSeatRequired || $[10] !== membersByKey || $[11] !== seatRequiredMessage || $[12] !== seatsAvailable || $[13] !== teamKeys) {
    checkSelection = function checkSelection(selectedOptions) {
      if (!isSeatRequired) {
        return SELECTION_WITHOUT_SEATS;
      }
      const selectedValues = getSelectedOptionValues(selectedOptions);
      const selection = getSelectionXgSeats({
        selectedValues,
        membersByKey,
        teamKeys,
        areAllMembersKnown: hasFetchedWorkspaceMembers,
        canGiveSeats: canManageXgSeats
      });
      const {
        blockedValues,
        membersToSeat,
        emailsToSeat
      } = selection;
      const seatKeys = getSeatKeys(selection);
      const hasEnoughSeats = hasSeatsFor(seatKeys.length);
      const isValidOtherwise = !areMembersLoading && !blockedValues.size;
      return {
        getChipColor: option => getXgSeatChipColor(getSelectedOptionValue(option), selection, hasEnoughSeats),
        helperText: getBlockedHelperText(blockedValues, seatKeys.length > 0),
        isValid: isValidOtherwise && hasEnoughSeats,
        giveSeatsCheckbox: seatKeys.length ? {
          numberOfMembers: seatKeys.length,
          seatsAvailable
        } : undefined,
        giveSeats: async () => {
          if (!seatKeys.length || !hasEnoughSeats) {
            return NO_FAILURES;
          }
          const givenSeats = await giveSeats({
            members: membersToSeat,
            emails: emailsToSeat
          });
          const failedKeys = new Set(givenSeats.failures.map(_temp));
          return {
            ...givenSeats,
            failedValues: new Set(selectedValues.filter(value => failedKeys.has(getSeatKey(value))))
          };
        }
      };
    };
    function getSeatKey(value_0) {
      const member = membersByKey.get(value_0) ?? membersByKey.get(value_0.toLowerCase());
      return member?.identity ?? value_0.toLowerCase();
    }
    function hasSeatsFor(numberOfPeople) {
      return seatsAvailable === undefined || numberOfPeople <= seatsAvailable;
    }
    function getBlockedHelperText(blockedValues_0, hasPeopleToSeat) {
      const reasons = new Set(blockedValues_0.values());
      if (hasPeopleToSeat && isOutOfSeats) {
        reasons.add("noSeat");
      }
      if (!reasons.size) {
        return;
      }
      const text = getBlockedMessage({
        reasons,
        isOutOfSeats: canManageXgSeats && isOutOfSeats,
        seatRequiredMessage
      });
      return {
        type: "error",
        text
      };
    }
    $[4] = areMembersLoading;
    $[5] = canManageXgSeats;
    $[6] = giveSeats;
    $[7] = hasFetchedWorkspaceMembers;
    $[8] = isOutOfSeats;
    $[9] = isSeatRequired;
    $[10] = membersByKey;
    $[11] = seatRequiredMessage;
    $[12] = seatsAvailable;
    $[13] = teamKeys;
    $[14] = checkSelection;
  } else {
    checkSelection = $[14];
  }
  const t3 = isSeatRequired && !canSelectMembersWithoutSeat ? NO_XG_SEAT_MESSAGE : undefined;
  const t4 = isSeatRequired ? TEAM_NOT_ALLOWED_MESSAGE : undefined;
  let t5;
  if ($[15] !== checkSelection || $[16] !== t3 || $[17] !== t4) {
    t5 = {
      memberWithoutSeatDisabledMessage: t3,
      teamDisabledMessage: t4,
      checkSelection
    };
    $[15] = checkSelection;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
}
function _temp(t0) {
  const {
    key
  } = t0;
  return key;
}